import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  TextField,
  Button,
  Typography,
  Link,
  Box,
  InputAdornment,
  IconButton,
  CircularProgress,
  MenuItem,
  Select,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  LocalLibrary as LibraryIcon,
  MenuBook as BookIcon,
  AutoStories as StoriesIcon,
  Bookmark as BookmarkIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useDirection } from "../../contexts/DirectionContext";
import { useLanguage } from "../../contexts/LanguageContext";

/* ── tiny floating book ── */
const FloatingBook = ({ sx }) => (
  <Box sx={{ position: "absolute", opacity: 0.1, animation: "float 7s ease-in-out infinite", ...sx }}>
    <BookIcon sx={{ fontSize: sx.size || 44, color: "#fff" }} />
  </Box>
);

/* ── language pill ── */
const LANGS = [
  { code: "en", flag: "🇬🇧", label: "EN" },
  { code: "fr", flag: "🇫🇷", label: "FR" },
  { code: "ar", flag: "🇸🇦", label: "AR" },
];

const Login = () => {
  const { t } = useTranslation();
  const { direction } = useDirection();
  const { currentLanguage, changeLanguage } = useLanguage();
  const isRTL = direction === "rtl";

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();
  const { showSnackbar } = useSnackbar();

  const handleChange = (e) =>
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await login(formData);
      showSnackbar(t("auth.signIn") + " ✓", "success");
      navigate("/dashboard");
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Login failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* shared field styles */
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      background: "#f8faff",
      fontSize: "0.95rem",
      "&:hover fieldset": { borderColor: "#2563eb" },
      "&.Mui-focused fieldset": { borderColor: "#2563eb", borderWidth: "2px" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#2563eb" },
    "& .MuiInputLabel-root": { ...(isRTL && { right: 32, left: "auto", transformOrigin: "right" }) },
    "& .MuiOutlinedInput-notchedOutline legend": { ...(isRTL && { textAlign: "right" }) },
  };

  return (
    <>
      <style>{`
        @keyframes float {
          0%,100% { transform: translateY(0) rotate(0deg); }
          40%      { transform: translateY(-20px) rotate(4deg); }
          70%      { transform: translateY(-10px) rotate(-3deg); }
        }
        @keyframes fadeIn {
          from { opacity:0; transform: translateY(16px); }
          to   { opacity:1; transform: translateY(0); }
        }
        @keyframes shine {
          0%   { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        html, body, #root { height: 100%; margin: 0; padding: 0; }
      `}</style>

      <Box
        dir={isRTL ? "rtl" : "ltr"}
        sx={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "row",
          fontFamily: isRTL ? "'Noto Kufi Arabic', 'Cairo', sans-serif" : "'Inter', sans-serif",
        }}
      >
        {/* ═══════════════════════════════════════
            LEFT / INNER  PANEL  — library branding
            ═══════════════════════════════════════ */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            width: "42%",
            flexShrink: 0,
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            background: "linear-gradient(160deg, #0a1628 0%, #0d2b55 50%, #0a3d7a 100%)",
          }}
        >
          {/* background glow circles */}
          {[
            { w: 320, h: 320, top: -100, left: -100, op: 0.06 },
            { w: 240, h: 240, bottom: -80, right: -80, op: 0.08 },
            { w: 180, h: 180, top: "40%", right: -50, op: 0.05 },
          ].map((c, i) => (
            <Box key={i} sx={{ position: "absolute", width: c.w, height: c.h, top: c.top, left: c.left, bottom: c.bottom, right: c.right, borderRadius: "50%", background: `rgba(255,255,255,${c.op})` }} />
          ))}

          {/* floating books */}
          <FloatingBook sx={{ top: "10%", left: "12%", size: 36, animationDelay: "0s" }} />
          <FloatingBook sx={{ top: "18%", right: "10%", size: 52, animationDelay: "1.8s" }} />
          <FloatingBook sx={{ top: "52%", left: "8%", size: 30, animationDelay: "3.2s" }} />
          <FloatingBook sx={{ bottom: "22%", right: "14%", size: 40, animationDelay: "0.9s" }} />
          <FloatingBook sx={{ bottom: "10%", left: "20%", size: 26, animationDelay: "2.5s" }} />

          {/* center content */}
          <Box sx={{ position: "relative", zIndex: 2, textAlign: "center", px: 5, animation: "fadeIn 0.8s ease" }}>
            {/* logo circle */}
            <Box sx={{ width: 88, height: 88, borderRadius: "22px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 3, backdropFilter: "blur(12px)", boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
              <LibraryIcon sx={{ fontSize: 46, color: "#fff" }} />
            </Box>

            {/* brand name */}
            <Typography sx={{ fontWeight: 900, fontSize: "2rem", letterSpacing: "-0.5px", mb: 0.5, background: "linear-gradient(90deg,#fff 0%,#93c5fd 50%,#fff 100%)", backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "shine 5s linear infinite" }}>
              eBiblio Space
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.9rem", mb: 4, letterSpacing: "0.04em" }}>
              {isRTL ? "مكتبتك الرقمية" : "Your Digital Library"}
            </Typography>

            {/* 3 stat pills */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 4 }}>
              {[
                { icon: <BookIcon sx={{ fontSize: 16 }} />, label: isRTL ? "كتب" : "Books" },
                { icon: <StoriesIcon sx={{ fontSize: 16 }} />, label: isRTL ? "مصادر" : "Resources" },
                { icon: <BookmarkIcon sx={{ fontSize: 16 }} />, label: isRTL ? "مكتبة" : "Library" },
              ].map((s) => (
                <Box key={s.label} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, color: "rgba(255,255,255,0.5)" }}>
                  {s.icon}
                  <Typography sx={{ fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>

            {/* quote card */}
            <Box sx={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "18px", p: 3, backdropFilter: "blur(8px)" }}>
              <Typography sx={{ color: "rgba(255,255,255,0.82)", fontStyle: "italic", fontSize: "0.9rem", lineHeight: 1.75 }}>
                {isRTL
                  ? '"غرفة بلا كتب كجسد بلا روح."'
                  : '"A room without books is like a body without a soul."'}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.38)", fontSize: "0.72rem", mt: 1.5, letterSpacing: "0.06em" }}>
                — Marcus Tullius Cicero
              </Typography>
            </Box>
          </Box>

          {/* bottom signature */}
          <Box sx={{ position: "absolute", bottom: 20, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.13)", borderRadius: "20px", px: 2.5, py: 0.7, backdropFilter: "blur(6px)" }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 8px #4ade80" }} />
              <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.7rem", letterSpacing: "0.12em" }}>
                ebibliospace.com
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ═══════════════════════════════════════
            RIGHT PANEL  — login form
            ═══════════════════════════════════════ */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "#ffffff",
            overflowY: "auto",
            position: "relative",
          }}
        >
          {/* language switcher — always at the logical end of the panel */}
          <Box sx={{ position: "absolute", top: 20, insetInlineEnd: 24 }}>
            <Select
              value={currentLanguage}
              onChange={(e) => changeLanguage(e.target.value)}
              size="small"
              variant="outlined"
              sx={{ borderRadius: "10px", fontSize: "0.8rem", height: 36, background: "#f8faff", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2563eb" } }}
            >
              {LANGS.map((l) => (
                <MenuItem key={l.code} value={l.code} sx={{ fontSize: "0.85rem", gap: 1 }}>
                  {l.flag} {l.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* mobile logo */}
          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.5, mb: 4 }}>
            <LibraryIcon sx={{ fontSize: 30, color: "#0d2b55" }} />
            <Typography sx={{ fontWeight: 800, fontSize: "1.3rem", color: "#0d2b55" }}>eBiblio Space</Typography>
          </Box>

          {/* form card */}
          <Box sx={{ width: "100%", maxWidth: 420, px: { xs: 3, sm: 5 }, animation: "fadeIn 0.6s ease" }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.9rem", color: "#0a1628", letterSpacing: "-0.5px", mb: 0.5, textAlign: "start" }}>
              {t("auth.welcomeBack")}
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: "0.93rem", mb: 4, textAlign: "start" }}>
              {t("auth.signInContinue")}
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate dir={isRTL ? "rtl" : "ltr"}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={t("auth.emailAddress")}
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "#94a3b8", fontSize: 19 }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t("auth.password")}
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "#94a3b8", fontSize: 19 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small" sx={{ color: "#94a3b8" }}>
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />

              <Button
                type="submit"
                fullWidth
                disabled={isSubmitting}
                sx={{
                  mt: 3, mb: 2.5,
                  py: 1.65,
                  borderRadius: "14px",
                  fontWeight: 700,
                  fontSize: "1rem",
                  textTransform: "none",
                  letterSpacing: "0.02em",
                  background: "linear-gradient(135deg, #0d2b55 0%, #2563eb 100%)",
                  boxShadow: "0 4px 22px rgba(37,99,235,0.32)",
                  transition: "all 0.25s",
                  "&:hover": { background: "linear-gradient(135deg, #0a1e3c 0%, #1d4ed8 100%)", boxShadow: "0 6px 28px rgba(37,99,235,0.48)", transform: "translateY(-1px)" },
                  "&:active": { transform: "translateY(0)" },
                  "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
                }}
              >
                {isSubmitting
                  ? <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}><CircularProgress size={18} sx={{ color: "#94a3b8" }} />{t("auth.signingIn")}</Box>
                  : t("auth.signIn")}
              </Button>

              <Box sx={{ textAlign: "center" }}>
                <Link component={RouterLink} to="/auth/register" sx={{ color: "#2563eb", fontWeight: 600, fontSize: "0.88rem", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                  {t("auth.noAccount")}
                </Link>
              </Box>
            </Box>

            {/* signature tag */}
            <Box sx={{ mt: 6, display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ flex: 1, height: "1px", background: "linear-gradient(to var(--line-dir, right), transparent, #e2e8f0)", ...(isRTL ? { "--line-dir": "left" } : {}) }} />
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.8, background: "#f8faff", border: "1px solid #e2e8f0", borderRadius: "20px", px: 2, py: 0.6, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                <LibraryIcon sx={{ fontSize: 13, color: "#2563eb" }} />
                <Typography sx={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 700, letterSpacing: "0.1em" }}>
                  eBiblio Space
                </Typography>
              </Box>
              <Box sx={{ flex: 1, height: "1px", background: isRTL ? "linear-gradient(to right, transparent, #e2e8f0)" : "linear-gradient(to left, transparent, #e2e8f0)" }} />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Login;
